<template>
  <GoogleAdPlaceholder
    v-if="showGAMAds"
    :ad-id="topPlacement[brand]"
    :page="pageType"
    :device="deviceType"
    :brand="productBrand"
    :is-u-s-a-region="isUSA"
    :is-wedding-registry="isRegistry"
    :env-brand="brand"
  />
  <component :is="devLinks" />
  <BrandBanner />
  <Breadcrumbs />

  <div class="grid-x margin-top-xs">
    <div
      class="top"
      :class="spacingClasses"
    >
      <ProductCard />

      <ProsZoneAVertical
        v-if="isProsAVisible"
        class="prosAWrapper"
        :class="prosASpacingClass"
      />

      <ProductPerks
        v-if="isProductPerksEnabled && isProductPerksTreatment2 && isMobile"
        class="margin-top-m"
      />
    </div>
  </div>
  <div :class="{'bottom': shoppableRedesignOptimization}">
    <ProsZoneBHorizontal v-if="isProsFVisible" />

    <div v-if="isMobile">
      <GoogleAdPlaceholder
        v-if="showGAMAds"
        :ad-id="inlinePlacement[brand]"
        :page="pageType"
        :brand="productBrand"
        :is-u-s-a-region="isUSA"
        :is-wedding-registry="isRegistry"
        :custom-style-class="spacingClasses"
        :env-brand="brand"
      />

      <SponsoredItems
        v-if="showSponsoredItems"
        :site-mon-config="siteMonConfig"
        :is-monetization-enabled="isMonetizationEnabled"
        :enable-impression-tags-for-s-m-products="enableImpressionTagsForSMProducts"
        :enable-click-tags-for-s-m-products="enableClickTagsForSMProducts"
        :carousel-open-in-new-tab-enabled="carouselOpenInNewTabEnabled"
        :is-mcom="isMcom"
        :selected-upc="selectedUpcIdAsString"
      />
    </div>

    <div v-if="!isShoppable">
      <LazyLoadWhenInViewport :root-margin="isPDPEnhancementUEnabled ? 1200 : 600">
        <ProsZoneBHorizontal v-if="prosZone && isPDPEnhancementWEnabled && !isStyliticsUnderPros && !isImageEntryEnabled2" />
      </LazyLoadWhenInViewport>

      <a name="outfitting-section-anchor" />

      <LazyLoadWhenInViewport :root-margin="1200">
        <Outfitting
          v-if="enableOutfittingWidget"
          v-bind="outfittingProps"
          :class="spacingClasses"
        />
        <ProductQuickviewWrapper
          v-if="enableProductQuickview"
        />
      </LazyLoadWhenInViewport>

      <LazyLoadWhenInViewport :root-margin="isPDPEnhancementUEnabled ? 1200 : 600">
        <ProsZoneBHorizontal v-if="prosZone && !isImageEntryEnabled2 && (!isPDPEnhancementWEnabled || isStyliticsUnderPros)" />
      </LazyLoadWhenInViewport>
    </div>

    <ProsZoneAVertical
      v-if="isProsGVisible"
      :class="spacingClasses"
    />

    <div v-if="!isMobile">
      <GoogleAdPlaceholder
        v-if="showGAMAds"
        :ad-id="inlinePlacement[brand]"
        :page="pageType"
        :device="deviceType"
        :brand="productBrand"
        :is-u-s-a-region="isUSA"
        :is-wedding-registry="isRegistry"
        :custom-style-class="spacingClasses"
        :env-brand="brand"
      />

      <SponsoredItems
        v-if="showSponsoredItems"
        :site-mon-config="siteMonConfig"
        :is-monetization-enabled="isMonetizationEnabled"
        :enable-impression-tags-for-s-m-products="enableImpressionTagsForSMProducts"
        :enable-click-tags-for-s-m-products="enableClickTagsForSMProducts"
        :carousel-open-in-new-tab-enabled="carouselOpenInNewTabEnabled"
        :is-mcom="isMcom"
        :selected-upc="selectedUpcIdAsString"
      />
    </div>

    <div v-if="!isShoppable">
      <LazyLoadWhenInViewport>
        <Syndigo
          v-if="syndigo"
          :class="spacingClasses"
        />
      </LazyLoadWhenInViewport>
      <ReviewSection
        v-if="showReviewSection"
        :class="spacingClasses"
      />
    </div>

    <LazyLoadWhenInViewport>
      <RecentlyViewedItems />
    </LazyLoadWhenInViewport>

    <GoogleAdPlaceholder
      v-if="showGAMAds && brand !== 'bcom' && deviceType !== 'mobile' && !isGiftWithPurchase"
      ad-id="googleAdOnPdpBottom"
      :page="pageType"
      :device="deviceType"
      :brand="productBrand"
      :is-u-s-a-region="isUSA"
      :is-wedding-registry="isRegistry"
      :custom-style-class="spacingClasses"
      :env-brand="brand"
    />
    <div v-if="isShoppable">
      <a name="outfitting-section-anchor" />

      <LazyLoadWhenInViewport :root-margin="1200">
        <Outfitting
          v-if="enableOutfittingWidget"
          v-bind="outfittingProps"
          :class="spacingClasses"
        />
        <LazyLoadWhenInViewport>
          <Syndigo
            v-if="syndigo"
            :class="spacingClasses"
          />
        </LazyLoadWhenInViewport>
        <ProductQuickviewWrapper
          v-if="enableProductQuickview"
        />
      </LazyLoadWhenInViewport>
      <ReviewSection
        v-if="showReviewSection"
        :class="spacingClasses"
      />
    </div>
  </div>
  <BrandBannerSecondary
    v-if="isChanelFlag"
    :class="spacingClasses"
  />
</template>

<script setup>
import {
  defineAsyncComponent, ref, onMounted, computed,
} from 'vue';
import { GoogleAdPlaceholder, SponsoredItems } from '@common-vue/site-monetization';
import { useUpc } from '@modules/upc';
import { useOutfittingData } from '@composables/useOutfittingData';
import { useFeatureEligibility, useProductFlags, useKillswitches } from '@modules/featureControl';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { brand } from 'config';
import { useBrand } from '@composables/useBrand';
import { usePageData } from '@composables/usePageData';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useProductType } from '@composables/useProductType';
import useSponsoredItems from '@composables/useSponsoredItems';
import useDevLinks from '@modules/dev/composables/useDevLinks';
import ProsZoneAVertical from '@components/pros/ProsZoneAVertical';
import { Breadcrumbs } from '@modules/breadcrumbs';
import ProductPerks from '@modules/productPerks/components/ProductPerks';
import { useExperiments } from '@composables/useExperiments';
import BrandBannerSecondary from '@components/brandBanner/BrandBannerSecondary.common';

import ProductCard from './ProductCard/ProductCard';
import BrandBanner from './brandBanner/BrandBanner.common';
import ReviewSection from './ReviewSection';
import LazyLoadWhenInViewport from './common/LazyLoadWhenInViewport';

const {
  productId, shoppingMode, regionCode,
} = usePageData();
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();
const { brandName } = useCommonProductGetters();
const { isGiftWithPurchase } = useProductType();
const { isHermesFlag, isChanelFlag } = useProductFlags();
const prosZone = computed(() => (isMcom && !isHermesFlag) || (isBcom && !isMobile.value));
const { killswitches } = useKillswitches();

const {
  syndigo, prosZoneA, showSponsoredItems,
  showGAMAds, showReviewSection,
  isShoppable, shoppableRedesignOptimization,
  isStyliticsUnderPros, isProductPerksEnabled,
} = useFeatureEligibility();

const {
  siteMonConfig,
  isMonetizationEnabled,
  enableImpressionTagsForSMProducts,
  enableClickTagsForSMProducts,
  carouselOpenInNewTabEnabled,
} = useSponsoredItems();

const { experimentFlags: { isProductPerksTreatment2, isImageEntryEnabled2 } } = useExperiments();

// TODO: imported files get loeaded twice. The issue should be fixed with SSR hydration fix on macys cli
const ProsZoneBHorizontal = defineAsyncComponent(() => import(/* webpackChunkName: 'ProsZoneB' */ '@components/pros/ProsZoneBHorizontal'));
const RecentlyViewedItems = defineAsyncComponent(() => import(/* webpackChunkName: 'RecentlyViewedItems' */ '@components/pros/RecentlyViewedItems'));
const Syndigo = defineAsyncComponent(() => import('./syndigo/Syndigo'));
const topPlacement = { mcom: 'googleAdOnPdpTop', bcom: 'googleAdOnPdp' };
const inlinePlacement = { mcom: 'googleAdOnPdp', bcom: 'googleAdOnPdpBottom' };
const deviceType = isMobile.value ? 'mobile' : 'desktop';
const pageType = 'product';
const isUSA = regionCode === 'US';
const isRegistry = shoppingMode === 'WEDDING_REGISTRY';
const productBrand = brandName.value;
const spacingClasses = shoppableRedesignOptimization ? 'small-margin-bottom-m large-margin-bottom-xl' : 'small-margin-bottom-xl large-margin-bottom-xxl';
const prosASpacingClass = shoppableRedesignOptimization ? 'margin-top-m' : 'margin-top-xl large-margin-top-z large-margin-left-xxl';
const { isPDPEnhancementWEnabled, isPDPEnhancementUEnabled } = killswitches;

// Pros
const isProsAVisible = computed(() => isMobile.value && prosZoneA && !isShoppable);
const isProsGVisible = computed(() => prosZoneA && isShoppable && isMobile.value);
const isProsFVisible = ((isMcom && !isHermesFlag) || isBcom) && isShoppable;
// Outfitting & ProductQuickview Slideout
const { outfittingProps, enableProductQuickview, enableOutfittingWidget } = useOutfittingData();
const Outfitting = defineAsyncComponent(() => import(/* webpackChunkName: 'Outfitting' */ '@common-vue/outfitting/src/components/features/stylitics/Outfitting'));
const ProductQuickviewWrapper = defineAsyncComponent(() => import(/* webpackChunkName: 'ProductQuickviewWrapper' */ '@components/slideoutContent/ProductQuickviewWrapper'));

/** @type {import('vue').Ref<import('vue').DefineComponent<any, any, any>?>} */
const devLinks = ref(null);
const { selectedUpcId } = useUpc();

// Convert selectedUpcId to a string
const selectedUpcIdAsString = computed(() => selectedUpcId.value?.toString() || '');

onMounted(async () => {
  devLinks.value = useDevLinks()?.vueComponent;
  try {
    const { addRecentlyViewedItems } = await import(/* webpackChunkName 'addRecentlyViewedItems' */ '@common-vue/pros-collection/src/utils/utils');
    addRecentlyViewedItems(productId);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to import add recently viewed item');
  }
});
</script>

<style lang="scss">
@import "~settingSCSS";
@include cta-buttons;
</style>

<style lang="scss" scoped>
@import "~settingSCSS";
.top {
  display: flex;
  flex-direction: column;
  width: 100%;

}
.bottom {
  :deep(.carousel-container) {
    .title-container {
      @include margin-bottom-s;
    }
  }
  :deep(.review-section) {
    h3 {
      @include margin-bottom-s;
    }
  }
  :deep(.outfitting) {
    h3 {
      @include margin-bottom-s;
    }
  }
  :deep(.pros-grid-header) {
    @include padding-bottom-s;
  }
}
@include breakpoint(large) {
  .top {
    flex-direction: row;
  }
}
</style>
